import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { Button } from '@amzn/ring-react-components/csr/components/Button';
import { Loader } from '@amzn/ring-react-components/csr/components/Loader';
import { Paragraph } from '@amzn/ring-react-components/csr/components/Paragraph';
import SirenPreference from '$src/components/SirenPreference';
import { handleBackToRingDashboard } from '$src/utils/handleBack';
import useLocationContext from '$src/hooks/useLocationContext';
import DevicesList from '$src/components/DevicesList';
import devicesImage from '$src/assets/img/onboardingDevices.png';
import OnboardingContainer from '$src/components/OnboardingContainer';
import useStores from '$src/hooks/useStores';
import { redirectToOnboardingIntroduction } from '$src/utils/redirect';
import { DevicesIcon } from '@ring-icons';

import './index.less';

function DeviceAuthorization() {
  const locationId = useLocationContext();
  const {
    sessionStore: { user, isMobile },
    devicesStore: { fetchDevices, notEligibleDevices, eligibleIndoorDevices, eligibleOutdoorDevices, subscribedDevices, bulkUpdate },
    statsStore: { sendStats },
  } = useStores();
  const [fetchingDevices, setFetchingDevices] = useState(true);
  const [enrollingDevices, setEnrollingDevices] = useState(false);
  const defaultEligibleDevices = eligibleOutdoorDevices.map((e) => ({ ...e, starkEnrolled: true }));
  const [devices, setDevices] = useState(subscribedDevices.length ? eligibleOutdoorDevices : defaultEligibleDevices);
  const localDevicesToEnroll = useRef(subscribedDevices.length ? [] : eligibleOutdoorDevices.map((e) => ({ ...e, starkEnrolled: true })));
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    async function getDevices() {
      await fetchDevices(locationId, user.user_id);
      setFetchingDevices(false);
    }
    if (locationId) getDevices();
  }, [fetchDevices, locationId, user.user_id, alert]);

  const localDevicesEnrolled = eligibleIndoorDevices.concat(devices).filter((e) => e.starkEnrolled);

  const updateLocalDevicesToEnroll = (device) => {
    if (localDevicesToEnroll.current.find((e) => e.doorbotId === device.doorbotId))
      localDevicesToEnroll.current = localDevicesToEnroll.current.filter((e) => e.doorbotId !== device.doorbotId);
    else localDevicesToEnroll.current.push(device);
  };

  const onPrimaryActionHandler = async () => {
    setEnrollingDevices(true);
    try {
      await bulkUpdate(localDevicesToEnroll.current, user.user_id, locationId);
      redirectToOnboardingIntroduction(locationId);
    } catch (e) {
      setAlert({ type: 'error', message: 'There was an error saving changes, please try again.' });
    }
    setEnrollingDevices(false);
  };

  const noSubscribedDevice = !localDevicesEnrolled.length;

  const handleSubscribe = async (device, subscribe) => {
    if (subscribe) {
      // eslint-disable-next-line no-param-reassign
      device.starkEnrolled = true;
    } else {
      // eslint-disable-next-line no-param-reassign
      device.starkEnrolled = false;
    }
    updateLocalDevicesToEnroll(device, subscribe);
    setDevices(devices.map((e) => (e.doorbotId === device.doorbotId ? device : e)));
  };

  return (
    <OnboardingContainer
      icon={DevicesIcon}
      title={'Select devices to enroll in Virtual Security Guard.'}
      headerTitle="Select Devices"
      image={devicesImage}
      warningOnClose
      handleClose={() => handleBackToRingDashboard(isMobile)}
      primaryActionHandler={onPrimaryActionHandler}
      alert={alert}
      className="device-authorization-page"
      handleBack={() => redirectToOnboardingIntroduction(locationId)}
      subtitle={
        <p>
          Virtual Guards from <b>Rapid Response</b> will monitor these devices when you arm the service by setting your location to <b>Home</b> or{' '}
          <b>Away</b> in the Ring App.
        </p>
      }
      footer={
        <>
          <Button
            color="primary"
            size="m"
            variant="filled"
            data-testid="onboarding-primary-action"
            onClick={onPrimaryActionHandler}
            disabled={noSubscribedDevice || enrollingDevices}
          >
            {enrollingDevices ? <Loader className="device-authorization-page__loader" /> : 'Continue'}
          </Button>

          {noSubscribedDevice && (
            <Paragraph size="l" weight="light" className="device-authorization-page__footer-text">
              At least one enrolled camera or doorbell is required.
            </Paragraph>
          )}
        </>
      }
    >
      <div className="device-authorization-page_content">
        <DevicesList
          notEligibleDevices={notEligibleDevices}
          eligibleOutdoorDevices={devices}
          eligibleIndoorDevices={eligibleIndoorDevices}
          subscribedDevices={subscribedDevices}
          sendStats={sendStats}
          user={user}
          loading={fetchingDevices}
          handleSubscribe={handleSubscribe}
        />
        <SirenPreference />
      </div>
    </OnboardingContainer>
  );
}

export default observer(DeviceAuthorization);
